import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { TaskAssigneeOptions } from 'pages/Tasks/constants'
import { type ChangeEvent, useEffect, useMemo, useState } from 'react'
import { getAdminEmails } from 'services'
import { deleteTaskTemplate, downloadTaskTemplates, getTasks, parseTaskImport } from 'services/apis/conditions'
import { downloadFile } from 'services/apis/documents'
import { svgLoading } from 'stories/assets'
import { Button, Input2, Tooltip } from 'stories/components'
import { confirm } from 'utils'

import { TaskDetails } from './details'
import { TaskImportModal } from './TaskImportModal'
import { TaskTemplates } from './TaskTemplates'
import type { ITaskTemplate } from './types'

export function Tasks() {
  const [view, setView] = useState<'list' | 'templates'>('list')
  const [query, setQuery] = useState('')
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [tasks, setTasks] = useState<Array<ITaskTemplate>>([])
  const [isEditing, setIsEditing] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [adminEmails, setAdminEmails] = useState<string[]>([])
  const [importRows, setImportRows] = useState<any[] | null>(null)

  const assigneeOptions = useMemo(() => {
    const options = { ...TaskAssigneeOptions() }
    adminEmails.forEach((email) => (options[email] = email))
    return options
  }, [adminEmails])

  useEffect(() => {
    refresh()
    getAdminEmails().then((emails: string[]) => setAdminEmails(emails))
  }, [])

  const refresh = () => {
    setIsLoading(true)
    getTasks()
      .then(({ data }) => {
        setTotal(data.length)
        setTasks(data)
      })
      .finally(() => setIsLoading(false))
  }

  if (isEditing)
    return (
      <TaskDetails
        lastUpdatedAt={lastUpdatedAt}
        defaults={selectedIndex == -1 ? null : tasks[selectedIndex]}
        onBack={() => setIsEditing(false)}
        onComplete={refresh}
        adminEmails={adminEmails}
      />
    )

  if (view === 'templates') {
    return (
      <div className="Tasks-container">
        <div className="flex items-center gap-4 mb-4">
          <button type="button" className="btn-link text-sm" onClick={() => setView('list')}>
            &larr; Back to Tasks
          </button>
        </div>
        <TaskTemplates />
      </div>
    )
  }

  const onAdd = () => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(-1)
    setIsEditing(true)
  }

  const onEdit = (index: number) => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(index)
    setIsEditing(true)
  }

  const onTrash = async (index: number) => {
    const { id, no } = tasks[index]
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to remove this task?
        <br />
        <span className="text-gray-600">Task No: {no}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setIsLoading(true)
    deleteTaskTemplate(id)
      .then(refresh)
      .catch(() => setIsLoading(false))
  }

  const onDownload = (ext: 'csv' | 'xlsx') => {
    downloadTaskTemplates(ext).then((res) => {
      downloadFile(`Tasks.${ext}`, res)
    })
  }

  const onImportFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsLoading(true)
    parseTaskImport(file)
      .then(({ data }) => {
        setImportRows(data)
      })
      .finally(() => {
        setIsLoading(false)
        event.target.value = ''
      })
  }

  return (
    <div className="Tasks-container">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-2xl font-bold flex items-center">
          Tasks
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h2>
        <button type="button" className="btn-link text-sm" onClick={() => setView('templates')}>
          Manage Templates
        </button>
      </div>
      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              onChange={(value) => setQuery(value)}
            />
          </div>
          <p className="ml-5">- {total} tasks</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32">
            <Button full onClick={onAdd}>
              Add
            </Button>
          </div>
          <button aria-label="Download CSV" className="btn-icon" type="button" onClick={() => onDownload('csv')}>
            <Tooltip message="Download CSV">
              <ArrowDownTrayIcon className="w-4 h-4" />
            </Tooltip>
          </button>
          <button aria-label="Download Excel" className="btn-icon" type="button" onClick={() => onDownload('xlsx')}>
            <Tooltip message="Download Excel">
              <ArrowDownTrayIcon className="w-4 h-4" />
            </Tooltip>
          </button>
          <label aria-label="Import Tasks" className="btn-icon cursor-pointer">
            <Tooltip message="Import Tasks">
              <ArrowUpTrayIcon className="w-4 h-4" />
            </Tooltip>
            <input className="hidden" type="file" accept=".csv,.xlsx,.xls" multiple={false} onChange={onImportFile} />
          </label>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="py-3 px-3">
                Description
              </th>
              <th scope="col" className="px-3">
                Note
              </th>
              <th scope="col" className="px-3">
                Assigned to
              </th>
              <th scope="col" className="px-3">
                Due days
              </th>
              <th scope="col" className="px-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="">
            {tasks.map((item: any, index) => {
              if (
                query &&
                !item.description.toLowerCase().includes(query.toLowerCase()) &&
                !item.notes.toLowerCase().includes(query.toLowerCase()) &&
                !`${item.no}`.toLowerCase().includes(query.toLowerCase())
              )
                return null
              return (
                <tr className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'}`} key={`${index}`}>
                  <td scope="row" className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {item.no}
                  </td>
                  <td className="px-3">{item.description}</td>
                  <td className="px-3">{item.notes}</td>
                  <td className="px-3">
                    <div>
                      {item.assignedTo.map((to: string) => TaskAssigneeOptions()[to] || to).join(', ')}
                    </div>
                    {!!item.assignedToSecondary?.length && (
                      <div className="text-gray-500 text-sm">
                        Secondary:{' '}
                        {item.assignedToSecondary.map((to: string) => TaskAssigneeOptions()[to] || to).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="px-3">{item.dueDays}</td>
                  <td className="px-3">
                    <span className="flex">
                      <span className="text-shade-blue p-1 hover-shadow1 cursor-pointer" onClick={() => onEdit(index)}>
                        <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                      </span>
                      <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onTrash(index)}>
                        <TrashIcon className="w-4 h-4"></TrashIcon>
                      </span>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!!importRows && (
        <TaskImportModal
          rows={importRows}
          tasks={tasks}
          assigneeOptions={assigneeOptions}
          onClose={() => setImportRows(null)}
          onComplete={refresh}
        />
      )}
    </div>
  )
}
